<template>
  <div class="footer-v2 mt30" role="contentinfo">
    
<FormKitLazyProvider config-file="true">
<ClientOnly>
      <SearchWidget v-if="showWidget" />
    </ClientOnly>
    <div class="footer-v2__city-list">
      <CityList />
    </div>
    <div
      class="footer-v2__background"
      :style="{
        backgroundColor: bgColor,
        color: textColor,
        backgroundImage: bgImage ? `url(${bgImage})` : 'none',
        backgroundSize: bgImageSize,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }"
    >
      <div class="footer-v2__container mt30">
        <div class="footer-v2__newsletter" role="region" aria-label="Newsletter subscription">
          <div class="footer-v2__info">
            <h3 class="footer-v2__info__title" role="heading">{{ newsletterTitle }}</h3>
            <p class="footer-v2__info__text mt10" v-sanitize-html="newsletterSubtitle?.replace(/\n/g, '<br />')"></p>
          </div>
          <div class="footer-v2__form">
            <FormKit
              id="newsLetterFooter"
              type="form"
              role="form"
              aria-label="Newsletter subscription form"
              :config="{ validationVisibility: 'submit' }"
              :actions="false"
              #default="{ value, state: { valid } }"
              :errors="state.errors"
              @submit="submit"
            >
              <Input
                v-model="state.form.email"
                class="w-100 mt10 mb0"
                type="email"
                :label="$t('footer_v2.news_letter.email_input')"
                validation="required|email"
                autocomplete="email"
              />
              <div class="footer-v2__form__input-submit">
                <Input
                  v-model="state.form.city"
                  type="text"
                  :label="newsletterCityPlaceholder"
                  autocomplete="address-level2"
                />
                <ClientOnly>
                  <SButton
                    type="submit"
                    :colors="{ backgroundColor: '#2C0266', borderColor: '#2C0266' }"
                    :shadow="false"
                    :full-width="isSMGrid"
                    :disabled="false"
                    :loading="loading"
                  >
                    {{ newsletterCta }}
                  </SButton>
                </ClientOnly>
              </div>
            </FormKit>
            <div class="footer-v2__form__success-container">
              <p
                class="footer-v2__form__sucess mt10"
                v-if="successfulRegistration"
                :style="{
                  backgroundColor: newsletterSuccessBgColor,
                  color: newsletterSuccessColor,
                  borderColor: newsletterSuccessColor,
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }"
              >
                <CheckCircle class="mr5" style="width: 14px; height: 14px" :fill="newsletterSuccessColor" />
                {{ newsletterSuccessText }}
              </p>
            </div>
          </div>
        </div>
        <div class="footer-v2__navigation" role="navigation" aria-label="Footer navigation">
          <div class="footer-v2__logos">
            <div class="footer-v2__logo-container">
              <NuxtTransformImage class="footer-v2__logo-container__logo" alt="footer-logo" :src="logo" />
            </div>
            <SocialBar class="footer-v2__social-bar" :social-links="socialLinks" />
          </div>
          <nav aria-label="Theme categories" v-if="linksSection">
            <ul class="footer-v2__theme-categories" role="list">
              <li v-for="item in linksSection.children" :key="item.name">
                <NuxtLink :to="item.to.path">
                  {{ item.name }}
                </NuxtLink>
              </li>
            </ul>
          </nav>
          <div class="footer-v2__company mt25" v-if="companySection">
            <div class="mb10">
              <p class="footer-v2__list-title">{{ companySection.title }}</p>
              <nav aria-label="Company">
                <ul class="footer-v2__list mt20 mb20" role="list">
                  <li class="mb5" v-for="item in companySection.children" :key="item.name">
                    <NuxtLink v-if="item.to" :to="item.to">
                      {{ item.name }}
                    </NuxtLink>
                    <NuxtLink v-else :href="item.href" external>
                      {{ item.name }}
                    </NuxtLink>
                  </li>
                </ul>
              </nav>
            </div>
            <div class="footer-v2__support" v-if="supportSection">
              <p class="footer-v2__list-title mb10">{{ supportSection.title }}</p>
              <nav aria-label="Support">
                <ul class="footer-v2__list mt20 mb20">
                  <li class="footer-v2__list__item mb5" v-for="item in supportSection.children" :key="item.name">
                    <NuxtLink v-if="item.to?.path === '/service-chat'" :to="item.to" external>
                      {{ item.name }}
                    </NuxtLink>
                    <NuxtLink v-else :to="item.to">
                      {{ item.name }}
                    </NuxtLink>
                  </li>
                </ul>
              </nav>
            </div>

            <div class="footer-v2__additional" v-if="additionalSection">
              <p class="footer-v2__list-title mb10">{{ additionalSection.title }}</p>
              <nav aria-label="Additional">
                <ul class="footer-v2__list mt20 mb20">
                  <li class="footer-v2__list__item mb5" v-for="item in additionalSection.children" :key="item.name">
                    <NuxtLink :to="item.to">
                      {{ item.name }}
                    </NuxtLink>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
        <div class="footer-v2__legal mt20" role="contentinfo" aria-label="Legal information">
          <p class="footer-v2__legal-title mb10">© {{ currentYear }} Realtainment GmbH | all rights reserved</p>
          <ul class="footer-v2__legal-list" v-if="legalSection">
            <li class="mb10" v-for="item in legalSection.children" :key="item.name">
              <NuxtLink :to="item.to">
                {{ item.name }}
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
</FormKitLazyProvider>

  </div>
</template>

<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { useMarketingStore } from '@/stores/marketing';
import { useStoryblokStore } from '@/stores/storyblok';
import { storeToRefs } from 'pinia';
import { reset } from '@formkit/core';

const SocialBar = defineAsyncComponent(() => import('@/components/molecules/SocialBar.vue'));
const Input = defineAsyncComponent(() => import('@/components/molecules/Input.vue'));
const SButton = defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue'));
const CityList = defineAsyncComponent(() => import('@/components/molecules/CityList.vue'));
const CheckCircle = defineAsyncComponent(() => import('@/assets/icons/icon--check-circle.svg?component'));
const SearchWidget = defineAsyncComponent(() => import('@/components/molecules/SearchWidget.vue'));
const NuxtTransformImage = defineAsyncComponent(() => import('@/components/molecules/NuxtTransformImage.vue'));

const route = useRoute();
const { $grid, $i18n } = useNuxtApp();

const marketingStore = useMarketingStore();
const { loading, successfulRegistration } = storeToRefs(marketingStore);
const storyblokStore = useStoryblokStore();
const { globalComponents } = storeToRefs(storyblokStore);

const state = reactive({
  form: {
    email: null,
    city: null
  }
});

const currentYear = ref('');

async function submit() {
  marketingStore.registerKlaviyoUser({
    ...state.form,
    klaviyo_list_id: globalComponents.value.footer?.newsletter_klaviyo_list_id,
    klaviyo_source: globalComponents.value.footer?.newsletter_klaviyo_source
  });
  reset('newsLetterFooter');
}

onMounted(() => {
  currentYear.value = new Date().getFullYear().toString();
});

const showWidget = computed(() => !isMobileGutschen.value && !isSurveyRoute.value);
const isSurveyRoute = computed(() => route.name === 'feedback');
const isGutschein = computed(() => route.path === '/malkurs-gutschein/gutscheinkarte-kaufen');

const isSMGrid = computed(() => $grid.sm);

const isMobileGutschen = computed(
  () => ($grid.breakpoint === 'xs' && isGutschein.value) || ($grid.breakpoint === 'sm' && isGutschein.value)
);

const linksSection = computed(() =>
  globalComponents.value.footer?.sections?.find((section: any) => section.key === 'links')
);

const companySection = computed(() =>
  globalComponents.value.footer?.sections.find((section: any) => section.key === 'company')
);

const supportSection = computed(() =>
  globalComponents.value.footer?.sections.find((section: any) => section.key === 'support')
);

const additionalSection = computed(() =>
  globalComponents.value.footer?.sections.find((section: any) => section.key === 'additional')
);

const legalSection = computed(() =>
  globalComponents.value.footer?.sections?.find((section: any) => section.key === 'legal')
);

const bgColor = computed(() => globalComponents.value.footer?.bg_image?.color || '#6E0AFA');
const bgImage = computed(() => {
  if (!$grid.md && globalComponents.value.footer?.bg_image?.mobileImage) {
    return globalComponents.value.footer?.bg_image?.mobileImage;
  }

  return globalComponents.value.footer?.bg_image?.desktopImage || false;
});
const bgImageSize = computed(() => {
  if (!$grid.md && globalComponents.value.footer?.bg_image?.mobileImageWidth) {
    return globalComponents.value.footer?.bg_image?.mobileImageWidth + 'px';
  }

  if (!$grid.md && globalComponents.value.footer?.bg_image?.mobileImageSize) {
    return globalComponents.value.footer?.bg_image?.mobileImageSize;
  }

  if (!$grid.md && globalComponents.value.footer?.bg_image?.desktopImageWidth) {
    return globalComponents.value.footer?.bg_image?.desktopImageWidth + 'px';
  }

  return false;
});
const textColor = computed(() => globalComponents.value.footer?.text_color);
const newsletterTitle = computed(() => globalComponents.value.footer?.newsletter_title);
const newsletterSubtitle = computed(() => globalComponents.value.footer?.newsletter_subtitle);
const newsletterCta = computed(
  () => globalComponents.value.footer?.newsletter_cta || $i18n.t('footer_v2.news_letter.register')
);
const newsletterSuccessText = computed(
  () => globalComponents.value.footer?.newsletter_success_text || $i18n.t('footer_v2.news_letter.success')
);
const newsletterSuccessColor = computed(
  () => globalComponents.value.footer?.newsletter_success_color?.color || 'var(--color-success-700)'
);
const newsletterSuccessBgColor = computed(
  () => globalComponents.value.footer?.newsletter_success_bg_color?.color || 'var(--color-success-100)'
);
const newsletterCityPlaceholder = computed(
  () => globalComponents.value.footer?.newsletter_city_placeholder || $i18n.t('footer_v2.news_letter.city_input')
);

const socialLinks = computed(() => globalComponents.value.footer?.socialLinks);
const logo = computed(() => globalComponents.value.footer?.logo);

const sanitizedNewsletterSubtitle = computed(() => {
  if (!newsletterSubtitle.value) return '';
  return newsletterSubtitle.value.replace(/\n/g, '<br />');
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/font-size.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.footer-v2 {
  &__city-list {
    height: 100px;
  }

  &__container {
    max-width: 1400px;
    padding: 40px 20px 0px;
    margin: auto;
    min-height: 1350px;

    @include bp(sm) {
      padding: 40px 30px 0px;
      min-height: 1000px;
    }

    @include bp(md) {
      padding: 60px 40px 0px;
      min-height: 850px;
    }

    @include bp(lg) {
      padding: 60px 40px 0px;
      min-height: 700px;
    }
  }

  &:deep(.art-input) {
    max-height: 44px !important;
    border-radius: 8px;
    margin-bottom: 10px;
  }

  &:deep(.n-button) {
    border-radius: 8px;
    height: 45px !important;
  }

  &:deep(.formkit-label) {
    color: var(--color-primary);
    opacity: 1;
  }

  &__newsletter {
    min-height: 300px;

    @include bp(sm-up) {
      display: flex;
      gap: 30px;
      align-items: flex-start;
    }

    @include bp(sm) {
      min-height: 300px;
    }

    @include bp(md) {
      min-height: 200px;
      gap: 60px;
    }

    @include bp(lg) {
      min-height: 150px;
      gap: 80px;
    }
  }

  &__info {
    height: 100%;

    @include bp(xs) {
      padding: 0 7px;
    }

    @include bp(md) {
      padding-top: 18px;
    }

    @include bp(sm) {
      flex: 1;
    }

    @include bp(md) {
      flex: 1.1;
    }

    @include bp(lg) {
      flex: 0.86;
    }

    &__title {
      @include font-size(30px);
      line-height: 36px;
      font-weight: 700;

      @include bp(sm) {
        @include font-size(36px);
        margin-bottom: 20px;
      }

      @include bp(md) {
        @include font-size(34px);
        margin-bottom: 18px;
      }
    }

    &__text {
      @include font-size(14px);
      line-height: 1.3;
      font-weight: 700;
      letter-spacing: 0;

      @include bp(sm-up) {
        font-weight: 500;
      }
    }
  }

  &__form {
    margin-top: 34px;
    height: 100%;

    @include bp(sm) {
      flex: 1;
    }

    @include bp(md) {
      flex: 0.9;
      margin-top: 0px;
    }

    @include bp(lg) {
      flex: 0.7;
    }

    &__input-submit {
      display: flex;
      gap: 10px;
      align-items: stretch;
      max-height: 45px;
    }

    /* Added container with fixed height to prevent layout shift */
    &__success-container {
      height: 24px;
      /* Set a fixed height based on the success message */
      margin-top: 10px;
    }

    &__sucess {
      @include font-size(14px);
      font-weight: 400;
      line-height: 150%;
      letter-spacing: 0.15px;
      padding: 2px 8px;
      border-radius: 16px;
      width: 110px;
      display: flex;
      align-items: center;
      padding-left: 5px;
    }
  }

  &__navigation {
    background: white;
    padding: 10px 20px;
    margin-bottom: 30px;
    border-radius: 16px;
    padding-bottom: 20px;
    min-height: 720px;
    color: var(--color-night-blue-900);

    @include bp(sm-up) {
      display: flex;
      margin-top: 30px;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    @include bp(sm) {
      min-height: 500px;
    }

    @include bp(md) {
      gap: 40px;
      min-height: 400px;
    }

    @include bp(lg) {
      display: flex;
      flex-wrap: wrap;
      min-height: 350px;
    }
  }

  &__logos {
    @include bp(xs) {
      padding-top: 17px;
    }

    @include bp(sm) {
      flex: 1.5;
      min-width: 100%;
    }

    @include bp(md) {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding-top: 13px;
      padding-left: 11px;
      max-width: 392px;
    }
  }

  &__logo-container {
    width: 286px;
    height: 63px;
    display: block;

    @include bp(sm-up) {
      width: 300px;
      height: 80px;
    }

    @include bp(md) {
      width: 347px;
      height: 80px;
    }

    &__logo {
      width: 100%;
      height: 100%;
    }
  }

  &__social-bar {
    margin-top: 20px;
    margin-bottom: 20px;
    align-content: space-around;
    height: 40px;
  }

  ul {
    list-style-type: none;
    padding-left: 0;
  }

  &__theme-categories {
    font-weight: 700;
    @include font-size(20px);
    line-height: 1.5;

    @include bp(xs) {
      padding: 0 4px !important;
    }
    @include bp(sm) {
      flex: 1;
    }

    @include bp(md) {
      flex: 1.3;
      line-height: 1.3;
      letter-spacing: -0.5px;
      margin: 14px 0 0;
      margin-top: 25px;
    }

    @include bp(lg) {
      @include font-size(24px);
      flex: 0.8;
    }

    li {
      @include bp(md) {
        margin-bottom: 4px;
      }
    }
  }

  &__company {
    @include bp(sm) {
      justify-content: space-between;
      width: 100%;
      display: flex;
    }

    @include bp(lg) {
      display: flex;
      flex: 1;
      display: flex;
      gap: 40px;
    }
  }

  &__list-title {
    font-weight: 400;
    @include font-size(12px);
    line-height: 1.5;
    color: var(--color-gray-700);
  }

  &__list {
    font-weight: 400;
    @include font-size(16px);
    line-height: 1.5;
  }

  &__legal {
    margin-left: 10px;

    @include bp(sm-up) {
      display: flex;
      justify-content: space-between;
      margin: 0px -10px;
    }

    @include bp(lg) {
      display: flex;
      justify-content: space-between;
      margin: 0px -30px;
    }
  }

  &__legal-title {
    font-weight: 500;
    @include font-size(12px);
    line-height: 1.5;
  }

  &__legal-list {
    margin-top: 25px;
    font-weight: 400;
    @include font-size(12px);
    line-height: 1.5;

    @include bp(sm-up) {
      margin-top: 0px;
      display: flex;
      justify-content: space-between;
      gap: 10px;
      align-items: baseline;
    }

    &__support {
      margin-top: 25px;

      @include bp(lg) {
        margin-top: 0px;
      }
    }
  }
}
</style>
